import { track } from "@amplitude/analytics-react-native";
import { useApolloClient, useQuery } from "@apollo/client";
import { useCallback } from "react";
import { AI_GENERATED_COVERS } from "src/api/featureFlags";
import GenerateAiCoverImageDocument from "src/api/graphql/__generated__/documents/GenerateAiCoverImageDocument";
import GetGatheringForThumbnailInfoDocument from "src/api/graphql/__generated__/documents/GetGatheringForThumbnailInfoDocument";
import SetGatheringThumbnailAiGenerationDocument from "src/api/graphql/__generated__/documents/SetGatheringThumbnailAiGenerationDocument";
import SetGatheringThumbnailDocument from "src/api/graphql/__generated__/documents/SetGatheringThumbnailDocument";
import SetGatheringThumbnailPublicAssetDocument from "src/api/graphql/__generated__/documents/SetGatheringThumbnailPublicAssetDocument";
import UploadGatheringThumbnailDocument from "src/api/graphql/__generated__/documents/UploadGatheringThumbnailDocument";
import PublicAssetVariant from "src/api/graphql/__generated__/enums/PublicAssetVariant";
import { parseGatheringThumbnailUrl } from "src/constants/GatheringThumbnail";
import { useGatheringStore } from "src/contexts/GatheringContext";
import toastError from "src/helpers/toastError";
import { useModal } from "src/modals";
import InternalModal from "src/modals/configs/InternalModal";
import {
	EditCoverAIGenerationTabConfig,
	EditCoverGallerySelection,
	EditCoverGalleryTabConfig,
} from "src/screens/(internal)/EditCoverGalleryModal/types";
import { getCurTime_MS } from "src/shared/helpers/timeHelpers";

const gatheringThumbnailSet = "gatheringThumbnail_set";
const gatheringThumbnailStart = "gatheringThumbnail_start";
const usePresentGatheringThumbnailEdit = ({ skipQuery = false }: { skipQuery?: boolean }) => {
	const { presentModal } = useModal<InternalModal>();
	const gatheringId = useGatheringStore((state) => state.gatheringId);
	const hasPhotos = useGatheringStore((state) => state.hasPhotos);
	const thumbnail = useGatheringStore((state) => state.thumbnail);
	const { data } = useQuery(GetGatheringForThumbnailInfoDocument, {
		fetchPolicy: "cache-and-network",
		variables: {
			input: {
				gatheringId,
			},
		},
		skip: skipQuery,
	});
	const apolloClient = useApolloClient();

	const presentGatheringThumbnailEdit = useCallback(() => {
		// Configuration definitions
		const configs: EditCoverGalleryTabConfig[] = [];

		// GatheringPhotos configuration
		const GatheringPhotosConfig: EditCoverGalleryTabConfig = {
			variant: "GatheringPhotos",
			gatheringId,
			itemKey: "GatheringPhotos",
			onSave: async ({ photoId }) => {
				track("gatheringThumbnail_photo_set", {
					gatheringId,
					photoId,
				});
				track(gatheringThumbnailSet, {
					gatheringId,
					photoId,
					variant: "photo",
				});
				await apolloClient.mutate({
					mutation: SetGatheringThumbnailDocument,
					variables: {
						input: {
							gatheringId,
							photoId,
						},
					},
				});
			},
		};
		// We only want to present with the GatheringPhotosConfig if the gathering has photos
		if (hasPhotos) {
			configs.push(GatheringPhotosConfig);
		}

		// Posters configuration
		const PostersConfig: EditCoverGalleryTabConfig = {
			variant: "Posters",
			itemKey: "Posters",
			publicAssetVariant: PublicAssetVariant.GatheringThumbnail,
			onSave: async ({ publicAssetId }) => {
				track("gatheringThumbnail_poster_set", {
					gatheringId,
					publicAssetId,
				});
				track(gatheringThumbnailSet, {
					gatheringId,
					publicAssetId,
					variant: "poster",
				});
				await apolloClient.mutate({
					mutation: SetGatheringThumbnailPublicAssetDocument,
					variables: {
						input: {
							gatheringId,
							assetId: publicAssetId,
						},
					},
				});
			},
		};
		configs.push(PostersConfig);

		// Upload configuration
		const UploadConfig: EditCoverGalleryTabConfig = {
			variant: "Upload",
			itemKey: "Upload",
			getUploadUrl: async () => {
				track("gatheringThumbnail_upload_start", {
					gatheringId,
				});
				track(gatheringThumbnailStart, {
					gatheringId,
					variant: "upload",
				});
				const { data } = await apolloClient.mutate({
					mutation: UploadGatheringThumbnailDocument,
					variables: {
						input: {
							gatheringId,
						},
					},
				});
				const url = data?.uploadGatheringThumbnail?.imageWriteUrl?.url;
				if (!url) {
					throw new Error("Could not get upload url");
				}
				return url;
			},
			onSave: async ({ imageWriteUrl }) => {
				track("gatheringThumbnail_upload_set", {
					gatheringId,
				});
				track(gatheringThumbnailSet, {
					gatheringId,
					variant: "upload",
				});
				const cur_MS = getCurTime_MS();
				while (getCurTime_MS() - cur_MS < 10_000) {
					await new Promise((resolve) => setTimeout(resolve, 500));
					const { data } = await apolloClient.query({
						query: GetGatheringForThumbnailInfoDocument,
						variables: {
							input: {
								gatheringId,
							},
						},
						fetchPolicy: "network-only",
					});
					const thumbnailId = data?.getGathering?.thumbnail?.thumbnailId;
					// If the thumbnailId matches the previous write URL, that means it was saved successfully
					if (thumbnailId && imageWriteUrl.includes(thumbnailId)) {
						return;
					}
				}
				toastError();
				throw new Error("Error saving new thumbnail");
			},
		};
		configs.push(UploadConfig);

		// AI Generated configuration
		if (AI_GENERATED_COVERS) {
			const AIGenerationConfig: EditCoverAIGenerationTabConfig = {
				variant: "AIGeneration",
				itemKey: "AIGeneration",
				getGeneratedImage: async (prompt) => {
					track("gatheringThumbnail_ai_generation_start", {
						gatheringId,
					});
					track(gatheringThumbnailStart, {
						gatheringId,
						variant: "aiGeneration",
					});

					const { data } = await apolloClient.mutate({
						mutation: GenerateAiCoverImageDocument,
						variables: {
							input: {
								prompt,
							},
						},
					});
					if (!data?.generateAICoverImage || !data.generateAICoverImage.imageUrl) {
						throw new Error("Could not generate AI image");
					}
					return {
						generationId: data.generateAICoverImage.generationId,
						imageUrl: data.generateAICoverImage.imageUrl,
					};
				},
				onSave: async ({ generationId }) => {
					track("gatheringThumbnail_ai_generation_set", {
						gatheringId,
					});
					track(gatheringThumbnailSet, {
						gatheringId,
						variant: "aiGeneration",
					});
					await apolloClient.mutate({
						mutation: SetGatheringThumbnailAiGenerationDocument,
						variables: {
							input: {
								gatheringId,
								generationId,
								waitForCompletion: true,
							},
						},
					});
				},
			};
			configs.push(AIGenerationConfig);
		}

		// Using existing selection if it exists
		const thumbnailUrl = parseGatheringThumbnailUrl({
			readUrl: thumbnail?.readUrl,
		});
		const selection: EditCoverGallerySelection | undefined = (() => {
			const thumbnailInfo = data?.getGathering?.thumbnail;

			const newThumbnailUrl = parseGatheringThumbnailUrl({
				readUrl: thumbnail?.readUrl,
			});
			if (thumbnailInfo && newThumbnailUrl) {
				if (thumbnailInfo.ownerId && thumbnailInfo.photoId) {
					return {
						variant: "GatheringPhotos",
						ownerId: thumbnailInfo.ownerId,
						photoId: thumbnailInfo.photoId,
						url: newThumbnailUrl,
						config: GatheringPhotosConfig,
					};
				}
				if (thumbnailInfo.publicAssetId) {
					return {
						variant: "Posters",
						publicAssetId: thumbnailInfo.publicAssetId,
						url: newThumbnailUrl,
						config: PostersConfig,
					};
				}
			}
			if (thumbnailUrl) {
				return {
					variant: "Initial",
					url: thumbnailUrl,
				};
			}
			return undefined;
		})();

		track("gatheringThumbnail_edit_open", {
			gatheringId,
		});

		// Present the modal
		presentModal("EditCoverGallery", {
			props: {
				configs,
				coverProfile: "Circle",
				selection,
			},
		});
	}, [
		apolloClient,
		data?.getGathering?.thumbnail,
		gatheringId,
		hasPhotos,
		presentModal,
		thumbnail?.readUrl,
	]);

	return presentGatheringThumbnailEdit;
};
export default usePresentGatheringThumbnailEdit;
